import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

// ── Data helpers ───────────────────────────────────────────────────────────────

const lifespan = (person) => {
  const b = person.birthYear;
  const d = person.deathYear;
  if (b && d) return `${b} – ${d}`;
  if (b) return `b. ${b}`;
  if (d) return `d. ${d}`;
  return null;
};

const formatDate = (dateStr, place) => {
  if (!dateStr && !place) return null;
  const parts = [dateStr, place].filter(Boolean);
  return parts.join(", ");
};

const MAX_ANCESTOR_DEPTH = 2;
const MAX_DESCENDANT_DEPTH = 2;
const DIAGRAM_WIDTH = 920;
const DIAGRAM_HEIGHT = 460;
const DIAGRAM_LANE_GAP = 220;
const DIAGRAM_MIN_NODE_STEP = 64;
const DIAGRAM_LABEL_EDGE_PAD = 74;

// Strip OCR trailing noise like "who was b" from spouse names
const cleanSpouseName = (raw) => {
  if (!raw) return raw;
  return raw
    .replace(/\s+who\s.*$/i, "")
    .replace(/\s+dau\s.*$/i, "")
    .replace(/\s+son\s.*$/i, "")
    .replace(/\s+was\s.*$/i, "")
    .trim();
};

const shortName = (name) => {
  if (!name) return "Unknown";
  if (name.length <= 24) return name;
  return `${name.slice(0, 23)}...`;
};

const normalizeForSearch = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildLocalDiagram = (selectedId, relationships, peopleById) => {
  if (!selectedId) return { nodes: [], links: [] };

  const id = String(selectedId);
  const parentMap = new Map();
  const childMap = new Map();

  relationships.forEach((r) => {
    if (r.type !== "parent-child") return;
    const from = String(r.fromId);
    const to = String(r.toId);

    if (!childMap.has(from)) childMap.set(from, new Set());
    childMap.get(from).add(to);

    if (!parentMap.has(to)) parentMap.set(to, new Set());
    parentMap.get(to).add(from);
  });

  const depthById = new Map();
  depthById.set(id, 0);

  const ancestorQueue = [{ id, depth: 0 }];
  while (ancestorQueue.length) {
    const current = ancestorQueue.shift();
    if (Math.abs(current.depth) >= MAX_ANCESTOR_DEPTH) continue;
    const parents = parentMap.get(current.id);
    if (!parents) continue;

    parents.forEach((pid) => {
      const nextDepth = current.depth - 1;
      const knownDepth = depthById.get(pid);
      if (
        knownDepth === undefined ||
        Math.abs(nextDepth) > Math.abs(knownDepth)
      ) {
        depthById.set(pid, nextDepth);
      }
      ancestorQueue.push({ id: pid, depth: nextDepth });
    });
  }

  const descendantQueue = [{ id, depth: 0 }];
  while (descendantQueue.length) {
    const current = descendantQueue.shift();
    if (current.depth >= MAX_DESCENDANT_DEPTH) continue;
    const children = childMap.get(current.id);
    if (!children) continue;

    children.forEach((cid) => {
      const nextDepth = current.depth + 1;
      const knownDepth = depthById.get(cid);
      if (
        knownDepth === undefined ||
        Math.abs(nextDepth) > Math.abs(knownDepth)
      ) {
        depthById.set(cid, nextDepth);
      }
      descendantQueue.push({ id: cid, depth: nextDepth });
    });
  }

  const nodes = [];
  depthById.forEach((depth, nodeId) => {
    const person = peopleById.get(nodeId);
    if (!person) return;
    nodes.push({
      id: String(person.id),
      depth,
      person,
    });
  });

  const visible = new Set(nodes.map((n) => n.id));
  const linkSet = new Set();
  const links = [];

  relationships.forEach((r) => {
    if (r.type !== "parent-child") return;
    const source = String(r.fromId);
    const target = String(r.toId);
    if (!visible.has(source) || !visible.has(target)) return;
    const key = `${source}->${target}`;
    if (linkSet.has(key)) return;
    linkSet.add(key);
    links.push({ source, target });
  });

  return { nodes, links };
};

const buildDiagramLayout = (diagramData) => {
  if (!diagramData.nodes.length) {
    return { nodes: [], links: [], height: DIAGRAM_HEIGHT };
  }

  const byDepth = new Map();
  diagramData.nodes.forEach((node) => {
    const depth = node.depth;
    if (!byDepth.has(depth)) byDepth.set(depth, []);
    byDepth.get(depth).push({ ...node });
  });

  byDepth.forEach((nodesAtDepth) => {
    nodesAtDepth.sort((a, b) => {
      const yearA = a.person.birthYear ?? Number.MAX_SAFE_INTEGER;
      const yearB = b.person.birthYear ?? Number.MAX_SAFE_INTEGER;
      if (yearA !== yearB) return yearA - yearB;
      return (a.person.name || "").localeCompare(b.person.name || "");
    });
  });

  const maxCount = Math.max(...Array.from(byDepth.values(), (n) => n.length));
  const height = Math.max(
    DIAGRAM_HEIGHT,
    110 + (maxCount - 1) * DIAGRAM_MIN_NODE_STEP,
  );
  const midX = DIAGRAM_WIDTH / 2;
  const nodeById = new Map();
  const laidOutNodes = [];

  Array.from(byDepth.keys())
    .sort((a, b) => a - b)
    .forEach((depth) => {
      const nodesAtDepth = byDepth.get(depth) || [];
      const count = nodesAtDepth.length;
      const step = DIAGRAM_MIN_NODE_STEP;
      const totalHeight = (count - 1) * step;
      const startY = height / 2 - totalHeight / 2;
      const x = midX + depth * DIAGRAM_LANE_GAP;

      nodesAtDepth.forEach((node, idx) => {
        const laidOut = {
          ...node,
          x,
          y: startY + idx * step,
        };
        nodeById.set(laidOut.id, laidOut);
        laidOutNodes.push(laidOut);
      });
    });

  const laidOutLinks = diagramData.links
    .map((link) => {
      const source = nodeById.get(link.source);
      const target = nodeById.get(link.target);
      if (!source || !target) return null;
      return {
        source,
        target,
      };
    })
    .filter(Boolean);

  return {
    nodes: laidOutNodes,
    links: laidOutLinks,
    height,
  };
};

// ── Component ─────────────────────────────────────────────────────────────────

const FamilyTree = () => {
  const [data, setData] = useState({ people: [], relationships: [], meta: {} });
  const [status, setStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setStatus("loading");
      try {
        const res = await fetch("/data/family/vary-family-tree.json", {
          cache: "no-store",
        });
        if (!res.ok) throw new Error(res.status);
        const payload = await res.json();
        if (!mounted) return;
        const people = Array.isArray(payload.people) ? payload.people : [];
        const relationships = Array.isArray(payload.relationships)
          ? payload.relationships
          : [];
        const meta =
          payload.meta && typeof payload.meta === "object" ? payload.meta : {};
        setData({ people, relationships, meta });
        setSelectedId((cur) => cur ?? people[0]?.id ?? null);
        setStatus("ready");
      } catch {
        if (mounted) setStatus("error");
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const peopleById = useMemo(() => {
    const map = new Map();
    data.people.forEach((p) => map.set(p.id, p));
    return map;
  }, [data.people]);

  const filtered = useMemo(() => {
    if (!query.trim()) return data.people;
    const tokens = normalizeForSearch(query).split(" ").filter(Boolean);
    return data.people.filter((p) => {
      const haystack = normalizeForSearch(
        [p.name, p.birthYear, p.deathYear, p.birthPlace, p.deathPlace, p.bio]
          .filter(Boolean)
          .join(" "),
      );
      return tokens.every((token) => haystack.includes(token));
    });
  }, [data.people, query]);

  const person = useMemo(
    () => (selectedId ? (peopleById.get(selectedId) ?? null) : null),
    [peopleById, selectedId],
  );

  const relatives = useMemo(() => {
    if (!person) return { parents: [], children: [], siblings: [] };
    const sid = String(person.id);
    const parents = data.relationships
      .filter((r) => r.type === "parent-child" && String(r.toId) === sid)
      .map((r) => peopleById.get(r.fromId))
      .filter(Boolean);
    const children = data.relationships
      .filter((r) => r.type === "parent-child" && String(r.fromId) === sid)
      .map((r) => peopleById.get(r.toId))
      .filter(Boolean);
    return { parents, children };
  }, [data.relationships, person, peopleById]);

  const diagramData = useMemo(
    () => buildLocalDiagram(selectedId, data.relationships, peopleById),
    [selectedId, data.relationships, peopleById],
  );

  const diagramLayout = useMemo(
    () => buildDiagramLayout(diagramData),
    [diagramData],
  );

  const provenance = useMemo(() => {
    const sections = data.meta?.provenance;
    return Array.isArray(sections) ? sections : [];
  }, [data.meta]);

  if (status === "loading")
    return (
      <Page>
        <Center>Loading family tree…</Center>
      </Page>
    );
  if (status === "error")
    return (
      <Page>
        <Center>
          Could not load family data.
          <br />
          Check /public/data/family/vary-family-tree.json.
        </Center>
      </Page>
    );

  return (
    <Page>
      <PageHeader>
        <h1>Vary Family Tree</h1>
        <Subtitle>
          Descendants of Samuel Vary of Stephentown, N.Y. ·{" "}
          {data.people.length.toLocaleString()} people recorded
        </Subtitle>
      </PageHeader>

      {provenance.length > 0 && (
        <ProvenancePanel>
          <SectionHeading>Narrative Genealogy Context</SectionHeading>
          <ProvenanceIntro>
            The following sections were extracted from prose pages that are not
            part of the numbered descendant entries.
          </ProvenanceIntro>
          <ProvenanceGrid>
            {provenance.map((section) => (
              <ProvenanceCard key={section.id || section.title}>
                <ProvenanceTitle>
                  {section.title || "Untitled Section"}
                </ProvenanceTitle>
                {Array.isArray(section.sourcePages) &&
                  section.sourcePages.length > 0 && (
                    <ProvenancePages>
                      Source page{section.sourcePages.length > 1 ? "s" : ""}:{" "}
                      {section.sourcePages.join(", ")}
                    </ProvenancePages>
                  )}
                {section.summary && (
                  <ProvenanceSummary>{section.summary}</ProvenanceSummary>
                )}
              </ProvenanceCard>
            ))}
          </ProvenanceGrid>
        </ProvenancePanel>
      )}

      <Grid>
        {/* Left: list panel */}
        <ListPanel>
          <SearchInput
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, place, year…"
          />
          <ListScroll>
            {filtered.map((p) => (
              <PersonRow
                key={p.id}
                type="button"
                $active={String(p.id) === String(selectedId)}
                onClick={() => setSelectedId(p.id)}
              >
                <RowName>{p.name || <em>Unknown</em>}</RowName>
                {lifespan(p) && <RowYears>{lifespan(p)}</RowYears>}
              </PersonRow>
            ))}
            {!filtered.length && (
              <EmptyState>No people match "{query}".</EmptyState>
            )}
          </ListScroll>
        </ListPanel>

        {/* Right: detail panel */}
        <DetailPanel>
          {person ? (
            <>
              <PersonName>{person.name || "Unknown"}</PersonName>

              {lifespan(person) && <Lifespan>{lifespan(person)}</Lifespan>}

              <FactGrid>
                {(person.birthDate || person.birthPlace) && (
                  <FactRow>
                    <FactLabel>Born</FactLabel>
                    <FactValue>
                      {formatDate(person.birthDate, person.birthPlace)}
                    </FactValue>
                  </FactRow>
                )}
                {(person.deathDate || person.deathPlace) && (
                  <FactRow>
                    <FactLabel>Died</FactLabel>
                    <FactValue>
                      {formatDate(person.deathDate, person.deathPlace)}
                    </FactValue>
                  </FactRow>
                )}
                {person.spouses?.length > 0 && (
                  <FactRow>
                    <FactLabel>
                      {person.spouses.length > 1 ? "Spouses" : "Spouse"}
                    </FactLabel>
                    <FactValue>
                      {person.spouses
                        .map(cleanSpouseName)
                        .filter(Boolean)
                        .join("; ")}
                    </FactValue>
                  </FactRow>
                )}
              </FactGrid>

              {person.bio && (
                <BioSection>
                  <SectionHeading>Biography</SectionHeading>
                  <BioText>{person.bio}</BioText>
                </BioSection>
              )}

              <FamilySection>
                <RelGroup>
                  <SectionHeading>Parents</SectionHeading>
                  <TagRow>
                    {relatives.parents.length ? (
                      relatives.parents.map((rel) => (
                        <RelButton
                          key={rel.id}
                          type="button"
                          onClick={() => setSelectedId(rel.id)}
                        >
                          {rel.name}
                          {rel.birthYear ? ` (${rel.birthYear})` : ""}
                        </RelButton>
                      ))
                    ) : (
                      <Muted>Not recorded</Muted>
                    )}
                  </TagRow>
                </RelGroup>

                <RelGroup>
                  <SectionHeading>
                    Children
                    {relatives.children.length
                      ? ` (${relatives.children.length})`
                      : ""}
                  </SectionHeading>
                  <TagRow>
                    {relatives.children.length ? (
                      relatives.children.map((rel) => (
                        <RelButton
                          key={rel.id}
                          type="button"
                          onClick={() => setSelectedId(rel.id)}
                        >
                          {rel.name}
                          {rel.birthYear ? ` (${rel.birthYear})` : ""}
                        </RelButton>
                      ))
                    ) : (
                      <Muted>None listed</Muted>
                    )}
                  </TagRow>
                </RelGroup>
              </FamilySection>

              <DiagramSection>
                <SectionHeading>Family Diagram (Local View)</SectionHeading>
                <DiagramHint>
                  Ancestors are on the left, descendants on the right. Click a
                  node to re-center.
                </DiagramHint>
                <LegendRow>
                  <LegendLine />
                  <LegendText>Parent</LegendText>
                  <LegendArrow>→</LegendArrow>
                  <LegendText>Child</LegendText>
                </LegendRow>
                {diagramLayout.nodes.length ? (
                  <DiagramFrame>
                    <DiagramSvg
                      viewBox={`0 0 ${DIAGRAM_WIDTH} ${diagramLayout.height}`}
                      role="img"
                      aria-label="Interactive local family diagram"
                    >
                      <defs>
                        <marker
                          id="parentChildArrow"
                          markerWidth="8"
                          markerHeight="8"
                          refX="6"
                          refY="4"
                          orient="auto"
                          markerUnits="strokeWidth"
                        >
                          <path
                            d="M0,0 L8,4 L0,8 z"
                            fill="rgba(141, 167, 245, 0.65)"
                          />
                        </marker>
                      </defs>

                      <DepthLane
                        x={DIAGRAM_WIDTH / 2 - DIAGRAM_LANE_GAP * 2}
                        y1="18"
                        y2={diagramLayout.height - 18}
                      />
                      <DepthLane
                        x={DIAGRAM_WIDTH / 2 - DIAGRAM_LANE_GAP}
                        y1="18"
                        y2={diagramLayout.height - 18}
                      />
                      <DepthLane
                        x={DIAGRAM_WIDTH / 2}
                        y1="18"
                        y2={diagramLayout.height - 18}
                        $focus
                      />
                      <DepthLane
                        x={DIAGRAM_WIDTH / 2 + DIAGRAM_LANE_GAP}
                        y1="18"
                        y2={diagramLayout.height - 18}
                      />
                      <DepthLane
                        x={DIAGRAM_WIDTH / 2 + DIAGRAM_LANE_GAP * 2}
                        y1="18"
                        y2={diagramLayout.height - 18}
                      />

                      <DepthLabel
                        x={DIAGRAM_LABEL_EDGE_PAD}
                        y="14"
                        textAnchor="start"
                      >
                        Ancestors
                      </DepthLabel>
                      <DepthLabel x={DIAGRAM_WIDTH / 2} y="14">
                        Selected
                      </DepthLabel>
                      <DepthLabel
                        x={DIAGRAM_WIDTH - DIAGRAM_LABEL_EDGE_PAD}
                        y="14"
                        textAnchor="end"
                      >
                        Descendants
                      </DepthLabel>

                      {diagramLayout.links.map((link, idx) => {
                        const source = link.source;
                        const target = link.target;
                        const sx = source.x ?? DIAGRAM_WIDTH / 2;
                        const sy = source.y ?? diagramLayout.height / 2;
                        const tx = target.x ?? DIAGRAM_WIDTH / 2;
                        const ty = target.y ?? diagramLayout.height / 2;
                        const mx = (sx + tx) / 2;
                        return (
                          <DiagramLinkPath
                            key={`${source.id}-${target.id}-${idx}`}
                            d={`M ${sx} ${sy} C ${mx} ${sy}, ${mx} ${ty}, ${tx} ${ty}`}
                            markerEnd="url(#parentChildArrow)"
                          />
                        );
                      })}

                      {diagramLayout.nodes.map((node) => {
                        const active = String(node.id) === String(selectedId);
                        const displayName = shortName(node.person.name);
                        const squeezeText = displayName.length > 20;
                        return (
                          <g
                            key={node.id}
                            transform={`translate(${node.x ?? 0}, ${node.y ?? 0})`}
                            onClick={() => setSelectedId(node.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <DiagramNodeCard
                              $active={active}
                              x="-82"
                              y="-19"
                              width="164"
                              height="38"
                              rx="10"
                            />
                            <NodeLabel
                              $active={active}
                              y="4"
                              textLength={squeezeText ? 146 : undefined}
                              lengthAdjust={
                                squeezeText ? "spacingAndGlyphs" : undefined
                              }
                            >
                              {displayName}
                            </NodeLabel>
                          </g>
                        );
                      })}
                    </DiagramSvg>
                  </DiagramFrame>
                ) : (
                  <Muted>Not enough linked relatives to draw a diagram.</Muted>
                )}
              </DiagramSection>
            </>
          ) : (
            <Center>Select a person from the list to see their details.</Center>
          )}
        </DetailPanel>
      </Grid>
    </Page>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const Page = styled.main`
  max-width: 1300px;
  margin: 0 auto;
  padding: 24px 16px 48px;
  color: #eef0ff;
  font-family: "Georgia", serif;
`;

const PageHeader = styled.header`
  margin-bottom: 20px;
  background: linear-gradient(
    180deg,
    rgba(8, 14, 36, 0.9),
    rgba(12, 20, 46, 0.85)
  );
  border: 1px solid rgba(141, 167, 245, 0.24);
  border-radius: 12px;
  padding: 16px 18px;

  h1 {
    margin: 0 0 6px;
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-family: "Cinzel", serif;
    letter-spacing: 0.03em;
    color: #f7d88b;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.35);
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: #d7e1ff;
  font-size: 0.95rem;
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: start;

  @media (min-width: 860px) {
    grid-template-columns: 320px 1fr;
  }
`;

const ProvenancePanel = styled.section`
  margin-bottom: 16px;
  background: rgba(12, 20, 46, 0.78);
  border: 1px solid rgba(141, 167, 245, 0.24);
  border-radius: 12px;
  padding: 12px 14px;
`;

const ProvenanceIntro = styled.p`
  margin: 0 0 10px;
  color: #adc0f7;
  font-size: 0.88rem;
  line-height: 1.45;
`;

const ProvenanceGrid = styled.div`
  display: grid;
  gap: 10px;

  @media (min-width: 980px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ProvenanceCard = styled.article`
  border: 1px solid rgba(141, 167, 245, 0.26);
  border-radius: 10px;
  background: rgba(16, 28, 62, 0.8);
  padding: 10px 11px;
`;

const ProvenanceTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 0.9rem;
  color: #f7d88b;
  line-height: 1.25;
`;

const ProvenancePages = styled.p`
  margin: 0 0 6px;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #7e93c5;
`;

const ProvenanceSummary = styled.p`
  margin: 0;
  color: #d9e1fb;
  font-size: 0.84rem;
  line-height: 1.45;
`;

const Panel = styled.article`
  background: rgba(8, 14, 36, 0.88);
  border: 1px solid rgba(141, 167, 245, 0.28);
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.35);
`;

const ListPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 860px) {
    position: sticky;
    top: 100px;
    max-height: calc(100vh - 130px);
  }
`;

const DetailPanel = styled(Panel)``;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(141, 167, 245, 0.5);
  background: rgba(14, 26, 59, 0.9);
  color: #eef0ff;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;

  &:focus {
    border-color: #8da7f5;
  }
`;

const ListScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 66vh;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(141, 167, 245, 0.3);
    border-radius: 99px;
  }
`;

const PersonRow = styled.button`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(247, 216, 139, 0.8)" : "rgba(141, 167, 245, 0.22)"};
  background: ${({ $active }) =>
    $active ? "rgba(60, 78, 138, 0.85)" : "rgba(18, 28, 60, 0.7)"};
  color: #eef0ff;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: rgba(45, 62, 112, 0.9);
    border-color: rgba(141, 167, 245, 0.55);
  }
`;

const RowName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowYears = styled.span`
  font-size: 0.8rem;
  color: #9ba8cc;
  white-space: nowrap;
  flex-shrink: 0;
`;

const PersonName = styled.h2`
  margin: 0 0 4px;
  font-size: clamp(1.25rem, 2.5vw, 1.7rem);
  font-family: "Cinzel", serif;
  color: #f7d88b;
  line-height: 1.25;
`;

const Lifespan = styled.p`
  margin: 0 0 14px;
  font-size: 1.05rem;
  color: #9baad6;
  letter-spacing: 0.04em;
`;

const FactGrid = styled.dl`
  margin: 0 0 20px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 16px;
  align-items: baseline;
`;

const FactRow = styled.div`
  display: contents;
`;

const FactLabel = styled.dt`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7e93c5;
  white-space: nowrap;
`;

const FactValue = styled.dd`
  margin: 0;
  font-size: 0.95rem;
  color: #dde3f8;
  line-height: 1.4;
`;

const BioSection = styled.section`
  margin-bottom: 24px;
`;

const SectionHeading = styled.h3`
  margin: 0 0 8px;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #7e93c5;
`;

const BioText = styled.p`
  margin: 0;
  font-size: 0.93rem;
  line-height: 1.65;
  color: #cdd5ef;
  white-space: pre-wrap;
`;

const FamilySection = styled.section`
  display: grid;
  gap: 16px;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DiagramSection = styled.section`
  margin-top: 22px;
`;

const DiagramHint = styled.p`
  margin: 0 0 10px;
  color: #91a2d8;
  font-size: 0.83rem;
`;

const LegendRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(141, 167, 245, 0.28);
  background: rgba(20, 32, 72, 0.55);
`;

const LegendLine = styled.span`
  width: 18px;
  height: 2px;
  background: rgba(141, 167, 245, 0.75);
`;

const LegendArrow = styled.span`
  color: #a4b3df;
  font-size: 0.9rem;
`;

const LegendText = styled.span`
  color: #a4b3df;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
`;

const DiagramFrame = styled.div`
  border: 1px solid rgba(141, 167, 245, 0.25);
  border-radius: 12px;
  background: linear-gradient(
    180deg,
    rgba(8, 14, 36, 0.5),
    rgba(14, 23, 50, 0.35)
  );
  overflow: hidden;
`;

const DiagramSvg = styled.svg`
  width: 100%;
  height: auto;
  display: block;
  min-height: 320px;
`;

const DepthLane = styled.line`
  stroke: ${({ $focus }) =>
    $focus ? "rgba(247, 216, 139, 0.24)" : "rgba(141, 167, 245, 0.17)"};
  stroke-width: ${({ $focus }) => ($focus ? 1.8 : 1)};
  stroke-dasharray: 4 5;
`;

const DepthLabel = styled.text`
  fill: #90a3d8;
  font-size: 10px;
  text-anchor: middle;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DiagramLinkPath = styled.path`
  stroke: rgba(141, 167, 245, 0.45);
  stroke-width: 1.3;
  fill: none;
`;

const DiagramNodeCard = styled.rect`
  fill: ${({ $active }) => ($active ? "#f7d88b" : "rgba(39, 57, 109, 0.95)")};
  stroke: ${({ $active }) => ($active ? "#fff4cf" : "#9eb4f7")};
  stroke-width: ${({ $active }) => ($active ? 2 : 1.2)};
`;

const NodeLabel = styled.text`
  fill: ${({ $active }) => ($active ? "#1e2442" : "#f3f6ff")};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-anchor: middle;
  pointer-events: none;
`;

const RelGroup = styled.div``;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const RelButton = styled.button`
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(141, 167, 245, 0.4);
  background: rgba(20, 32, 72, 0.85);
  color: #c9d4f5;
  font-size: 0.85rem;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: rgba(55, 75, 140, 0.9);
    border-color: #8da7f5;
  }
`;

const Muted = styled.span`
  color: #6070a0;
  font-size: 0.88rem;
  font-style: italic;
`;

const Center = styled.p`
  text-align: center;
  color: #8da7f5;
  padding: 32px 0;
`;

const EmptyState = styled.p`
  color: #6070a0;
  font-size: 0.9rem;
  padding: 8px 0;
  font-style: italic;
`;

export default FamilyTree;
